/**
 * Phase 23.11 — Tablet de consentimientos.
 *
 * Pantalla kiosko para que el paciente firme un consentimiento informado
 * que ha sido "enviado a la tablet" desde el dashboard.
 *
 * Flujo:
 *   1. Polling cada 4s a /api/public/consent-tablet/next (sesión de tablet o de gestión).
 *   2. Cuando hay una firma pendiente: muestra nombre paciente + tipo de
 *      consent + doctor + fecha del consent.
 *   3. Botón "Ver documento" → iframe con el PDF (preview).
 *   4. Botón "Firmar" → recuadro de firma (FirmaDedo) a todo el ancho del iPad.
 *   5. Submit → POST /api/public/consent-tablet/{pid}/sign con la firma
 *      en base64. Tras éxito vuelve a estado "Esperando…".
 *
 * NADIE FIRMA LO QUE NO HA LEÍDO (23/9/2026). El recuadro de firma solo se
 * enseña para el documento que se acaba de enseñar para leer (`firmaDe`), y
 * cada vez que cambia el documento en pantalla se vuelve a la lectura y el
 * trazo se va con el recuadro. Ver `claveDocumento` y `submitSignature`.
 *
 * Si no hay sesión activa se redirige a /login (gestionado por useAuth).
 * Entran las DOS cuentas de tablet (`tablet` y `tablet_consent`: desde el
 * 29/9/2026 la tablet es una cuenta con dos modos, ver `lib/modoTablet`) y
 * gestión (el botón «Modo tablet» de Consentimientos). A la tablet se le
 * ofrece «Cambiar de modo», que vuelve al selector sin contraseña.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { esCuentaDeTablet, olvidarModoTablet, RUTA_SELECTOR_TABLET } from '../lib/modoTablet';
import { api, mensajeError } from '../lib/api';
import { toast } from 'sonner';
import { useClinica } from '../lib/clinica';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import FirmaDedo from '../components/FirmaDedo';
import VisorPdfPublico from '../components/VisorPdfPublico';
import { Loader2, FileText, CheckCircle2, RefreshCw, FilePen, AlertTriangle } from 'lucide-react';
import LogoClinica from '../components/LogoClinica';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL_MS = 4000;
// Lo que se queda en pantalla «Firma registrada» antes de pasar al siguiente.
const DONE_MS = 4000;
// Tras un CAMBIO de documento a media lectura o firma, lo mínimo que tiene que
// estar a la vista antes de poder pasar a firmarlo (2ª pasada, 23/9/2026). No
// es el tiempo de leerlo —eso es cosa de la paciente—: es que dos toques
// seguidos no la lleven al recuadro de un papel que ha visto un segundo.
export const LECTURA_MINIMA_TRAS_CAMBIO_MS = 5000;
const SESION_CADUCADA = 'La sesión de esta tablet ha caducado: avisa en recepción';

/* ---------------------------------------------------------------------------
 * EL IPAD SE PONE AL DÍA SOLO (3ª pasada, 23/9/2026)
 * ---------------------------------------------------------------------------
 * El iPad de consentimientos no se cierra nunca: tras un despliegue seguía
 * DÍAS con el código de antes, porque nada lo recargaba, y los arreglos de la
 * firma no le llegaban. La web publica su versión en `/version.txt` (la
 * escribe Dockerfile.web con la misma APP_VERSION que se compila en
 * REACT_APP_VERSION) y Caddy la sirve sin caché. Esta página la mira cada
 * pocos minutos y, si ha cambiado, recarga, pero SOLO en un momento seguro:
 * sin documento en pantalla o leyendo sin haber tocado nada; nunca con el
 * recuadro de firma abierto, con una firma en camino ni con el «Firma
 * registrada» a la vista. Si no es buen momento, espera a que lo sea.
 *
 * Una consulta que falla NO recarga. Y ojo: sin version.txt (en local) el
 * servidor contesta el index.html con un 200; eso no es una versión.
 * ------------------------------------------------------------------------- */
export const VERSION_CADA_MS = 3 * 60 * 1000;

/** La versión con la que se compiló ESTE código ('' en local y en los tests sin ella). */
const versionCompilada = () => String(process.env.REACT_APP_VERSION || '').trim();

/** La versión publicada ahora, o null si no se ha podido saber. */
async function leerVersionPublicada() {
  try {
    const r = await fetch(`/version.txt?_=${Date.now()}`, { cache: 'no-store' });
    if (!r || !r.ok) return null;
    const texto = String(await r.text()).trim();
    // «1.0.0+abc1234»: una palabra corta. Lo demás (HTML, vacío…) no lo es.
    if (!texto || texto.length > 80 || /[\s<>]/.test(texto)) return null;
    return texto;
  } catch {
    return null;
  }
}

const recargarDeVerdad = () => window.location.reload();

/* ---------------------------------------------------------------------------
 * LA FIRMA DICE DESDE QUÉ PANTALLA Y QUÉ DOCUMENTO (4ª pasada, 24/9/2026)
 * ---------------------------------------------------------------------------
 * El servidor solo acepta la firma del iPad si trae el `consent_id` del
 * documento en pantalla y el `pantalla_id` de ESTA carga de la página, y si
 * esa pantalla pidió ESE PDF (`?pantalla=`) después de la última vez que el
 * documento llegó a la cola. Así la firma no puede salir de una pestaña vieja
 * ni de un documento que la pantalla no ha cargado. Sin esos campos contesta
 * 409 «Esta tablet tiene una versión anterior del programa: recárgala…».
 *
 * El id se genera UNA vez por carga de la página (vive en el módulo: una
 * recarga lo cambia; volver a montar la página, no). Cada visor nuevo vuelve
 * a pedir el PDF de verdad: la API contesta con `Cache-Control: no-store`
 * (server.py), así que el servidor se entera de cada carga.
 * ------------------------------------------------------------------------- */
export function nuevoIdDePantalla() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Sin randomUUID (Safari antiguo, o fuera de HTTPS): un UUID v4 igual, con
  // getRandomValues si lo hay y, si no, con Math.random.
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const x = Array.from(b, (n) => n.toString(16).padStart(2, '0')).join('');
  return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20)}`;
}

let idDeEstaPantalla = '';
function pantallaId() {
  if (!idDeEstaPantalla) idDeEstaPantalla = nuevoIdDePantalla();
  return idDeEstaPantalla;
}

/** El 409 del servidor que dice que esta página es de una versión anterior. */
const pideRecargarLaPagina = (e) => (e?.response?.status === 409
  && /versi[oó]n anterior|rec[aá]rgal[ao]/i.test(mensajeError(e, '')));

const AVISO_RECARGA = 'Esta tablet tiene una versión anterior del programa: se va a recargar sola '
  + 'ahora mismo. Después, vuelve a leer el documento y fírmalo.';

/**
 * ¿El visor ha cargado la PÁGINA DE ERROR del servidor en vez del PDF? (23/9)
 *
 * El `load` del iframe salta igual con una página de error, y eso se daba por
 * «documento cargado». Se detecta en POSITIVO: la página de error lleva una
 * marca que solo lleva ella (`<meta name="clinicpilot-error">` y el cuerpo
 * con `id="clinicpilot-error"`, backend/services/pagina_error_archivo.py).
 * Solo con esa marca se bloquea «Continuar». Todo lo demás —un documento sin
 * la marca, del tipo que sea, o uno que no se puede leer (otro origen)— vale
 * como cargado, igual que antes.
 *
 * AL REVÉS NO (4ª pasada): se probó a exigir que lo cargado dijera
 * `application/pdf`, pero el visor de PDF de Safari del iPad puede no
 * declararlo, y entonces «Continuar» se quedaría bloqueado en TODOS los
 * consentimientos. Aquí no se pierde seguridad: el servidor ya exige haber
 * servido el PDF para aceptar la firma, y su 409 devuelve a la lectura
 * (`volverALaLectura`).
 *
 * Tampoco se pide el PDF aparte con fetch para mirarlo: un HEAD no existe en
 * esa ruta (405) y un GET contaría para el servidor como «PDF servido» aunque
 * el visor enseñara un error.
 */
function esLaPaginaDeError(marco) {
  try {
    const doc = marco.contentDocument;
    if (!doc) return false;
    return Boolean(doc.querySelector?.('meta[name="clinicpilot-error"]')
      || doc.getElementById?.('clinicpilot-error'));
  } catch {
    return false;
  }
}

/** El 409 del servidor que pide abrir y leer el PDF antes de firmar. */
const pideLeerElDocumento = (e) => (e?.response?.status === 409
  && /abr[ae] y lee|lee(r)? el documento/i.test(mensajeError(e, '')));

/**
 * QUÉ DOCUMENTO HAY EN PANTALLA: su petición de firma Y su consentimiento
 * (23/9/2026).
 *
 * Antes el sondeo solo miraba el `pending_id` para decidir si «era otro», y al
 * cambiarlo NO tocaba el paso: si la paciente estaba en el recuadro de firma,
 * el siguiente de la cola aparecía directamente en el recuadro. Con la wifi
 * de la clínica pasaba así: firma de «Blefaroplastia» guardada, respuesta
 * perdida, la pantalla seguía en la firma; a los 4 segundos el sondeo traía
 * «Anestesia» y la ponía en el MISMO recuadro, con el trazo de antes todavía
 * dentro. Un toque en «Firmar y enviar» y quedaba firmada «Anestesia», que
 * nadie le había enseñado para leer.
 *
 * Ahora la pantalla guarda de qué documento abrió el recuadro (`firmaDe`) y
 * solo lo enseña si es ESTE; cualquier cambio de documento vuelve a la
 * lectura y se lleva el trazo. Si es el mismo, no se toca nada: el trazo se
 * queda.
 */
export function claveDocumento(doc) {
  return doc ? `${doc.pending_id || ''}|${doc.consent_id || ''}` : '';
}

const mismoDocumento = (a, b) => !!a && !!b && claveDocumento(a) === claveDocumento(b);

/**
 * ¿El fallo deja la firma en el aire? Sin respuesta (wifi, timeout) o con un
 * 5xx del proxy no se sabe si llegó al servidor. Un 4xx sí es su respuesta:
 * ha dicho que no y por qué, y no se le vuelve a mandar.
 */
const sinSaberSiLlego = (e) => !e?.response || (e.response.status || 0) >= 500;

/**
 * La sesión del iPad ha caducado (o ya no es de tablet). Las rutas
 * /api/public/ no llevan al login al dar 401 (lib/api), así que la pantalla
 * se quedaba preguntando cada 4 s y, tras un fallo de la firma, decía
 * «Comprueba la conexión», que no es lo que pasa. Eso no lo arregla la
 * paciente: lo arregla recepción volviendo a entrar.
 */
const esSesionCaducada = (e) => [401, 403].includes(e?.response?.status);

export default function PublicConsentPage({ recargar = recargarDeVerdad, versionCadaMs = VERSION_CADA_MS }) {
  const clinicaDatos = useClinica();
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);   // { pending_id, patient, template, doctor_name, ... }
  const [polling, setPolling] = useState(true);
  const [busy, setBusy] = useState(false);
  // De qué documento está abierto el recuadro de firma (`claveDocumento`), o
  // null mientras se lee. Solo lo pone el botón «Continuar a la firma» de la
  // pantalla de lectura, con el documento que esa pantalla enseña.
  const [firmaDe, setFirmaDe] = useState(null);
  // La firma que se acaba de guardar ({ paciente, documento, ya }) mientras
  // se enseña «Firma registrada/guardada». Va aparte del documento en
  // pantalla a propósito: antes el aviso colgaba de `pending` y el sondeo lo
  // cambiaba por el siguiente, con el nombre del siguiente en el «¡firmado!».
  const [hecho, setHecho] = useState(null);
  const [lastSignedAt, setLastSignedAt] = useState(null);
  // Phase 23.13 — Diagnóstico: estado del último polling para que el usuario
  // pueda VER si la tablet se está comunicando bien con el servidor.
  const [pollStatus, setPollStatus] = useState({ at: null, ok: null, msg: '' });
  // Clave del documento cuyo PDF se ha visto cargar (el iframe avisa) o que
  // se ha abierto entero. Hasta entonces no se puede pasar a firmarlo.
  const [pdfCargado, setPdfCargado] = useState('');
  // El documento ha CAMBIADO a media lectura o firma: { clave, nombre,
  // minimoCumplido }. Da el aviso fijo y los segundos de espera.
  const [cambio, setCambio] = useState(null);
  const [sesionCaducada, setSesionCaducada] = useState(false);
  // Clave del documento cuyo visor ha cargado algo que NO es su PDF (una
  // página de error), y cuántas veces se ha vuelto a montar el visor.
  const [pdfFallido, setPdfFallido] = useState('');
  const [visorN, setVisorN] = useState(0);
  // El servidor ha devuelto a la lectura de este documento, y por qué:
  // { clave, texto } (409 «Abre y lee el documento antes de firmarlo»).
  const [avisoLectura, setAvisoLectura] = useState(null);
  // Clave del documento en el que la paciente ya ha tocado algo: a partir
  // de ahí, la recarga por versión nueva espera.
  const [tocadoEn, setTocadoEn] = useState('');
  const [versionNueva, setVersionNueva] = useState(false);
  // El servidor ha dicho que esta página es de una versión anterior (409
  // «recárgala»): hay que recargar, y se dice en pantalla.
  const [recargaObligada, setRecargaObligada] = useState(false);
  const sigRef = useRef(null);
  const temporizadorHecho = useRef(null);
  // El `pending_id` que se está enseñando, para decírselo a la cola.
  const idEnPantalla = useRef('');
  // La clave del documento en pantalla, para quien no se entera por render
  // (el aviso de que el visor se ha llevado el foco).
  const claveVista = useRef('');
  // El ÚLTIMO documento que se enseñó, aunque luego la cola haya estado
  // vacía: A → nada → B también es un cambio (3ª pasada). Solo se olvida al
  // firmar o saltar, cuando lo siguiente es lo natural.
  const ultimoMostrado = useRef('');
  const versionAlCargar = useRef(versionCompilada());
  const yaRecargando = useRef(false);
  // Número de la última pregunta a la cola: la respuesta de una anterior que
  // llega tarde se tira.
  const peticionCola = useRef(0);

  /**
   * Polling de la cola. Devuelve lo que ha contestado ({ datos } o
   * { error }, con `vieja` si ya no es la última) para la firma que ha
   * fallado, que necesita saber qué dice la cola.
   *
   * 2ª pasada (23/9/2026):
   *  · le dice al servidor qué documento hay en pantalla (`?actual=`), para
   *    que no se lo cambie por otro mientras siga pendiente;
   *  · solo manda la ÚLTIMA respuesta. Con la wifi de la clínica las
   *    respuestas llegan desordenadas, y una vieja que llegaba tarde volvía a
   *    poner en pantalla un documento ya firmado;
   *  · 401/403: la sesión del iPad ha caducado. Se dice, y se deja de
   *    preguntar (ver `esSesionCaducada`).
   */
  const fetchNext = useCallback(async () => {
    const at = new Date().toLocaleTimeString('es-ES');
    peticionCola.current += 1;
    const mia = peticionCola.current;
    const actual = idEnPantalla.current;
    try {
      const r = await api.get('/api/public/consent-tablet/next',
        actual ? { params: { actual } } : undefined);
      const datos = r.data || {};
      if (mia !== peticionCola.current) return { datos, vieja: true };
      setSesionCaducada(false);
      setPolling(true);
      if (datos.available) {
        // El MISMO documento se queda con el objeto de antes: no se toca la
        // pantalla ni el trazo. Otro documento entra, y entra para LEERLO
        // (el recuadro es de `firmaDe`, que no es el suyo).
        setPending(prev => (mismoDocumento(prev, datos) ? prev : datos));
        setPollStatus({ at, ok: true, msg: `Consent pendiente: ${datos.template?.name || ''}` });
      } else {
        setPending(null);
        setPollStatus({ at, ok: true, msg: 'Sin consents pendientes' });
      }
      return { datos };
    } catch (e) {
      if (mia !== peticionCola.current) return { error: e, vieja: true };
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'desconocido';
      setPollStatus({
        at,
        ok: false,
        msg: status ? `HTTP ${status} · ${detail}` : `Error: ${detail}`,
      });
      if (esSesionCaducada(e)) {
        setSesionCaducada(true);
        setPolling(false);
      }
      return { error: e };
    }
  }, []);

  // Cambia el documento en pantalla → a leer, y sin trazo. El recuadro ya no
  // se pinta (su `firmaDe` es de otro documento); esto lo deja apuntado para
  // que, si el primero volviera a la cola, tampoco saliera directo a firmar.
  // Si el último documento que se enseñó era OTRO, es un cambio a media
  // lectura o firma: se avisa y se espera. «El último» aunque entre medias la
  // cola haya estado vacía (3ª pasada, 23/9/2026): antes solo contaba si justo
  // antes había un documento en pantalla, y A → cola vacía → B —aunque B fuera
  // de otra paciente— entraba sin aviso y sin los segundos. Tras firmar o
  // saltar se olvida (`ultimoMostrado`): lo siguiente es lo natural.
  const claveEnPantalla = claveDocumento(pending);
  const nombreEnPantalla = pending?.template?.name || '';
  const pendingIdEnPantalla = pending?.pending_id || '';
  useEffect(() => {
    idEnPantalla.current = pendingIdEnPantalla;
    claveVista.current = claveEnPantalla;
    setFirmaDe(null);
    sigRef.current?.clear?.();
    if (!claveEnPantalla) {
      setCambio(null);
      return;
    }
    const antes = ultimoMostrado.current;
    ultimoMostrado.current = claveEnPantalla;
    setCambio(antes && antes !== claveEnPantalla
      ? { clave: claveEnPantalla, nombre: nombreEnPantalla, minimoCumplido: false }
      : null);
  }, [claveEnPantalla, nombreEnPantalla, pendingIdEnPantalla]);

  // ¿Hay versión nueva publicada? (ver VERSION_CADA_MS). La de referencia es
  // la compilada; sin ella (en local), la primera que conteste bien.
  useEffect(() => {
    let vivo = true;
    const comprobar = async () => {
      const publicada = await leerVersionPublicada();
      if (!vivo || !publicada) return;
      if (!versionAlCargar.current) {
        versionAlCargar.current = publicada;
        return;
      }
      if (publicada !== versionAlCargar.current) setVersionNueva(true);
    };
    comprobar();
    const id = setInterval(comprobar, versionCadaMs);
    return () => { vivo = false; clearInterval(id); };
  }, [versionCadaMs]);

  // Leer el PDF dentro del visor no se ve desde aquí (los toques se quedan en
  // el iframe), pero al tocarlo la página pierde el foco y el iframe se lo
  // lleva: eso también es «haber tocado».
  useEffect(() => {
    const alIrseElFoco = () => {
      if (document.activeElement?.tagName === 'IFRAME' && claveVista.current) {
        setTocadoEn(claveVista.current);
      }
    };
    window.addEventListener('blur', alIrseElFoco);
    return () => window.removeEventListener('blur', alIrseElFoco);
  }, []);

  // Recargar por versión nueva, SOLO en un momento seguro. Si ahora no lo es,
  // este efecto vuelve a mirar en cuanto cambie cualquiera de las piezas.
  //
  // Con el 409 «recárgala» (4ª pasada) la misma regla, menos una pieza: la
  // lectura ya tocada NO hace esperar. Esta pantalla no puede firmar nada
  // hasta recargarse (cada intento daría el mismo 409), así que esperar a que
  // la cola se vacíe la dejaría atascada delante de un documento que no puede
  // firmar. Lo que sí se respeta: ni con una firma en camino, ni con el
  // recuadro abierto (se cierra al recibir el 409), ni con el «firmado».
  const firmandoAhora = !!pending && firmaDe === claveEnPantalla;
  const sinNadaEnCurso = !busy && !hecho;
  const momentoSeguro = sinNadaEnCurso && (sesionCaducada
    || (!firmandoAhora && (!pending || tocadoEn !== claveEnPantalla)));
  const momentoSeguroObligada = sinNadaEnCurso && !firmandoAhora;
  useEffect(() => {
    if (yaRecargando.current) return;
    if ((versionNueva && momentoSeguro) || (recargaObligada && momentoSeguroObligada)) {
      yaRecargando.current = true;
      recargar();
    }
  }, [versionNueva, momentoSeguro, recargaObligada, momentoSeguroObligada, recargar]);

  // Los segundos mínimos tras un cambio. El aviso se queda; lo que se levanta
  // es la espera.
  useEffect(() => {
    if (!cambio || cambio.minimoCumplido) return undefined;
    const t = setTimeout(() => {
      setCambio(c => (c && c.clave === cambio.clave ? { ...c, minimoCumplido: true } : c));
    }, LECTURA_MINIMA_TRAS_CAMBIO_MS);
    return () => clearTimeout(t);
  }, [cambio]);

  // El «Firma registrada» no puede seguir contando con la página cerrada.
  useEffect(() => {
    const temporizador = temporizadorHecho;
    return () => clearTimeout(temporizador.current);
  }, []);

  // Con `polling` apagado (sesión caducada) no se pregunta NADA, tampoco una
  // vez al apagarse: si no, cada 401 volvía a lanzar la pregunta. Se vuelve a
  // encender con una respuesta buena (el botón «Recargar»).
  useEffect(() => {
    const esKiosco = user && (esCuentaDeTablet(user)
      || ['admin', 'direccion', 'administrativo'].includes((user?.role || '').toLowerCase()));
    if (!esKiosco || !polling) return undefined;
    fetchNext();
    const id = setInterval(fetchNext, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, polling, fetchNext]);

  // Auth gates
  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando…</div>;
  }
  if (!user) return <Navigate to="/login" replace state={{ from: '/firma-consent' }} />;
  // Entran las cuentas de tablet (las dos, 29/9/2026) Y gestión (el botón
  // "Modo tablet" de Consentimientos lleva aquí para pasar la tablet al
  // entorno de firma).
  const cuentaDeTablet = esCuentaDeTablet(user);
  const rolKiosco = cuentaDeTablet
    || ['admin', 'direccion', 'administrativo'].includes((user?.role || '').toLowerCase());
  if (!rolKiosco) {
    return <Navigate to="/" replace />;
  }

  // `?pantalla=`: el servidor apunta que ESTA pantalla cargó ESTE PDF (4ª
  // pasada). La ruta sigue siendo la misma, así que la página de error
  // marcada y el «ha cargado» del visor no cambian.
  const pdfUrl = pending
    ? `${BACKEND_URL}/api/public/consent-tablet/${pending.pending_id}/pdf?pantalla=${encodeURIComponent(pantallaId())}`
    : '';
  // El recuadro de firma, SOLO para el documento que se abrió para leer.
  const puedeFirmar = !!pending && firmaDe === claveDocumento(pending);
  // «Continuar a la firma», solo con el PDF de ESTE documento cargado y, si
  // acaba de cambiar, pasados los segundos mínimos.
  const cambioEnPantalla = cambio && cambio.clave === claveEnPantalla ? cambio : null;
  const pdfListo = !!pending && pdfCargado === claveEnPantalla;
  const pdfMal = !!pending && pdfFallido === claveEnPantalla;
  const esperaMinima = !!cambioEnPantalla && !cambioEnPantalla.minimoCumplido;
  const esperaLectura = recargaObligada
    ? 'La tablet se está poniendo al día: enseguida podrás continuar a la firma.'
    : pdfMal
    ? 'No se ha podido cargar el documento. Pulsa «Volver a cargar el documento» o «Abrir el documento completo»; si sigue sin verse, avisa en recepción.'
    : !pdfListo
      ? 'Podrás continuar a la firma cuando el documento se haya abierto y cargado.'
      : esperaMinima
        ? 'Ha cambiado el documento: tómate unos segundos para leerlo antes de continuar a la firma.'
        : '';
  const avisoLecturaEnPantalla = avisoLectura && avisoLectura.clave === claveEnPantalla
    ? avisoLectura.texto : '';

  // Algo se ha tocado en el documento que hay en pantalla (ver la recarga).
  const marcarTocado = () => { if (claveEnPantalla) setTocadoEn(claveEnPantalla); };

  /**
   * Vuelta a la LECTURA de `doc` (3ª pasada, 23/9/2026): el servidor exige
   * haber servido su PDF antes de firmarlo y contesta 409 «Abre y lee el
   * documento antes de firmarlo». Antes eso se quedaba en un aviso encima del
   * recuadro de firma, y volver a pulsar daba el mismo 409: atascada. Ahora
   * se cierra el recuadro, el visor se monta de nuevo (vuelve a pedir el
   * PDF), «Continuar» espera a que cargue y el motivo se queda a la vista.
   */
  /**
   * 409 «recárgala» (4ª pasada): el servidor no acepta firmas de esta carga
   * de la página. El recuadro se cierra (ese trazo no puede entrar), se dice
   * en pantalla y la recarga sale sola en cuanto no haya nada en curso (ver
   * `momentoSeguroObligada`). Tras recargar, la paciente vuelve a la lectura
   * del mismo documento, con un visor que lo pide para la pantalla nueva.
   */
  const pedirRecarga = () => {
    setFirmaDe(null);
    setRecargaObligada(true);
  };

  const volverALaLectura = (doc, motivo) => {
    const clave = claveDocumento(doc);
    setFirmaDe(null);
    setPdfCargado(c => (c === clave ? '' : c));
    setPdfFallido('');
    setAvisoLectura({ clave, texto: motivo });
  };

  /** La firma de `doc` está en el servidor (recién guardada o ya estaba). */
  const firmaGuardada = (doc, ya) => {
    setLastSignedAt(new Date().toLocaleTimeString('es-ES'));
    setPolling(true);   // reanudamos polling
    // Lo que venga ahora de la cola es OTRO documento: entra para leerlo. Y
    // es lo siguiente tras firmar, no un cambio a media lectura. Cualquier
    // pregunta a la cola que saliera antes de esta firma ya no vale.
    peticionCola.current += 1;
    ultimoMostrado.current = '';
    setPending(null);
    setFirmaDe(null);
    setCambio(null);
    setHecho({ paciente: doc.patient?.name || '', documento: doc.template?.name || '', ya: !!ya });
    toast.success(ya ? 'Firma guardada. ¡Gracias!' : 'Firma registrada. ¡Gracias!');
    // Tras 4 s, lo que toque: se pregunta ANTES de quitar el aviso, para no
    // enseñar un instante la espera (o el documento de antes) entre medias.
    clearTimeout(temporizadorHecho.current);
    temporizadorHecho.current = setTimeout(async () => {
      await fetchNext();
      setHecho(null);
    }, DONE_MS);
  };

  /**
   * TRAS UN ERROR AL FIRMAR SE PREGUNTA EL ESTADO (23/9/2026). Antes se
   * enseñaba el error y la pantalla se quedaba en la firma; el sondeo hacía
   * el resto, y lo hacía mal (ver `claveDocumento`). Ahora:
   *   · se pregunta a la cola (y lo que conteste se pone en pantalla: si es
   *     otro documento, entra para LEERLO);
   *   · si la cola sigue en este mismo documento, la firma no llegó: se queda
   *     en el recuadro con su trazo y el motivo, y volver a pulsar es seguro;
   *   · si ni la cola contesta, no se sabe nada y se dice así;
   *   · si la cola ya va por otro y el fallo dejó la firma en el aire, se le
   *     pregunta al servidor por ESTA firma mandándosela otra vez: el
   *     servidor firma por `pending_id` —nunca otro documento— y a una
   *     petición que ya tiene su firma le contesta 200 con `ya_firmado` sin
   *     tocarla. Así la paciente lee «Firma guardada» en vez de quedarse con
   *     la duda. Solo se hace cuando la cola ya no la tiene pendiente: si la
   *     tiene, reenviar es cosa de ella (nada de reintentos a ciegas).
   * 2ª pasada: si el servidor HA CONTESTADO que no (4xx: 409 cancelada, ya
   * no existe…), su motivo va primero y siempre, conteste o no la cola
   * después; y nunca el texto técnico de axios («Request failed with status
   * code 409»). Con la sesión caducada, eso y nada más.
   * Devuelve la respuesta buena de esa pregunta, o null.
   */
  const despuesDeUnError = async (errorFirma, doc, reenviar) => {
    const nombre = doc.template?.name || 'el consentimiento';
    if (esSesionCaducada(errorFirma)) {
      setSesionCaducada(true);
      setPolling(false);
      return null;
    }
    if (pideRecargarLaPagina(errorFirma)) {
      pedirRecarga();
      return null;
    }
    if (!sinSaberSiLlego(errorFirma)) {
      // El servidor ya dijo que no, y por qué: SU motivo. Luego se sigue a la
      // cola (si ya va por otro documento, entra para leerlo).
      const motivo = mensajeError(errorFirma, 'no se ha podido firmar. Avisa en recepción.');
      const { datos } = await fetchNext();
      // Un 409 con ESTE documento todavía pendiente (o el de «ábrelo y léelo»
      // sin respuesta de la cola) no se arregla volviendo a pulsar: se vuelve
      // a su lectura, con su PDF y el motivo a la vista (3ª pasada).
      const sigueEnLaCola = !!datos?.available && mismoDocumento(datos, doc);
      if (errorFirma.response.status === 409
          && (sigueEnLaCola || (!datos && pideLeerElDocumento(errorFirma)))) {
        volverALaLectura(doc, motivo);
        return null;
      }
      toast.error(`«${nombre}»: ${motivo}`, { duration: 10000 });
      return null;
    }
    const { datos, error } = await fetchNext();
    if (error) {
      if (esSesionCaducada(error)) {
        setSesionCaducada(true);
        setPolling(false);
        return null;
      }
      toast.error(`No hemos podido confirmar si la firma de «${nombre}» ha llegado. `
        + 'Comprueba la conexión y vuelve a pulsar «Firmar y enviar»: si ya se guardó, no se firmará dos veces.',
      { duration: 10000 });
      return null;
    }
    if (datos?.available && mismoDocumento(datos, doc)) {
      // Sin su detalle a propósito: un 5xx trae «Internal Server Error», que
      // no le dice nada a la paciente.
      toast.error(`No se ha podido guardar la firma de «${nombre}». `
        + 'Vuelve a pulsar «Firmar y enviar»: si ya se guardó, no se firmará dos veces.');
      return null;
    }
    try {
      return await reenviar();
    } catch (e) {
      if (esSesionCaducada(e)) {
        setSesionCaducada(true);
        setPolling(false);
        return null;
      }
      if (pideRecargarLaPagina(e)) {
        pedirRecarga();
        return null;
      }
      toast.error(e?.response && !sinSaberSiLlego(e)
        ? `«${nombre}»: ${mensajeError(e, 'no se ha podido firmar. Avisa en recepción.')}`
        : `No hemos podido confirmar la firma de «${nombre}», y ya no está pendiente en esta tablet. `
          + 'El personal puede comprobarlo en la ficha de la paciente.',
      { duration: 10000 });
      return null;
    }
  };

  const submitSignature = async () => {
    // El documento que tiene delante, fijado ahora: lo que haga el sondeo
    // mientras tanto no cambia lo que se firma.
    const doc = pending;
    if (!doc || !puedeFirmar) return;
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Por favor, firma dentro del recuadro antes de enviar.');
      return;
    }
    const signature_b64 = sigRef.current.getCanvas().toDataURL('image/png');
    // Qué documento y desde qué pantalla (4ª pasada): los mismos en el envío
    // y en el reenvío tras una respuesta perdida, porque es la misma firma.
    const cuerpo = { signature_b64, consent_id: doc.consent_id, pantalla_id: pantallaId() };
    const enviar = () => api.post(
      `/api/public/consent-tablet/${doc.pending_id}/sign`,
      cuerpo
    );
    setBusy(true);
    try {
      let r;
      try {
        r = await enviar();
      } catch (e) {
        r = await despuesDeUnError(e, doc, enviar);
      }
      if (r?.data?.ok) {
        firmaGuardada(doc, r.data.ya_firmado);
      } else if (r) {
        toast.error('No se pudo guardar la firma. Vuelve a intentarlo.');
      }
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    if (!pending) return;
    if (!window.confirm('¿Saltar este consentimiento? Volverá a estar disponible más tarde si lo reenvían desde el dashboard.')) return;
    try {
      await api.post(`/api/public/consent-tablet/${pending.pending_id}/skip`);
      toast.info('Saltado.');
      // Lo que saliera antes de saltarlo ya no vale (podría traerlo otra vez).
      // Y lo siguiente es lo natural tras saltar: no es un «ha cambiado».
      peticionCola.current += 1;
      ultimoMostrado.current = '';
      setPending(null);
      fetchNext();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo saltar'));
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB] text-foreground" data-testid="public-consent-page">
      <header className="bg-white border-b border-border px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          {/* El logo de Configuración; sin logo, nada: el nombre va debajo. */}
          <LogoClinica className="h-10 w-auto" />
          <div>
            <h1 className="text-[#13231E] font-display font-bold text-lg leading-tight">Tablet de Consentimientos</h1>
            <p className="text-xs text-muted-foreground">Firma del paciente · {clinicaDatos.clinic_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Al selector de modo, sin contraseña (29/9/2026): solo la cuenta del iPad. */}
          {cuentaDeTablet && (
            <Button
              variant="ghost" size="sm" className="text-muted-foreground"
              onClick={() => { olvidarModoTablet(); navigate(RUTA_SELECTOR_TABLET, { replace: true }); }}
              data-testid="cambiar-modo-tablet"
            >
              Cambiar de modo
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={fetchNext} data-testid="public-consent-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Recargar
          </Button>
        </div>
      </header>

      {/* Cualquier toque en el documento en pantalla lo marca como «tocado»:
          la recarga por versión nueva ya no lo interrumpe (3ª pasada). */}
      <main
        className="max-w-5xl mx-auto px-6 py-8"
        onPointerDown={marcarTocado}
        onTouchStart={marcarTocado}
        onClick={marcarTocado}
        onKeyDown={marcarTocado}
      >
        {/* El servidor ha dicho que esta pantalla es de una versión anterior:
            se dice, fijo, mientras llega la recarga (4ª pasada). */}
        {recargaObligada && (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2 rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3 text-base font-medium text-amber-900"
            data-testid="public-consent-recarga"
          >
            <RefreshCw className="w-5 h-5 mt-0.5 shrink-0 animate-spin" />
            <span>{AVISO_RECARGA}</span>
          </div>
        )}

        {sesionCaducada && (
          <SesionCaducadaCard onLogout={logout} />
        )}

        {!sesionCaducada && hecho && (
          <DoneCard hecho={hecho} />
        )}

        {!sesionCaducada && !hecho && !pending && (
          <IdleWaitingCard onLogout={logout} lastSignedAt={lastSignedAt} pollStatus={pollStatus} user={user} />
        )}

        {/* `key`: cada documento con su propio visor, para que el «ha
            cargado» que llegue sea el de SU PDF y no el del anterior; y un
            visor nuevo al pulsar «Volver a cargar el documento». */}
        {!sesionCaducada && !hecho && pending && !puedeFirmar && (
          <ReviewCard
            key={`${claveEnPantalla}#${visorN}`}
            pending={pending}
            pdfUrl={pdfUrl}
            cambio={cambioEnPantalla}
            avisoLectura={avisoLecturaEnPantalla}
            esperaLectura={esperaLectura}
            pdfMal={pdfMal}
            onPdfListo={() => {
              setPdfCargado(claveDocumento(pending));
              setPdfFallido('');
            }}
            onPdfFallido={() => {
              const clave = claveDocumento(pending);
              setPdfFallido(clave);
              setPdfCargado(c => (c === clave ? '' : c));
            }}
            onRecargarVisor={() => { setPdfFallido(''); setVisorN(n => n + 1); }}
            onProceedToSign={() => {
              if (esperaLectura) return;
              setAvisoLectura(null);
              setFirmaDe(claveDocumento(pending));
            }}
            onSkip={skip}
          />
        )}

        {!sesionCaducada && !hecho && puedeFirmar && (
          <SignatureCard
            pending={pending}
            sigRef={sigRef}
            busy={busy}
            onBack={() => setFirmaDe(null)}
            onSubmit={submitSignature}
          />
        )}
      </main>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================
function IdleWaitingCard({ onLogout, lastSignedAt, pollStatus, user }) {
  const okBadge = pollStatus?.ok === true;
  const errBadge = pollStatus?.ok === false;
  return (
    <Card className="border-2 border-dashed border-[#2E6E5E]/40 bg-white" data-testid="public-consent-idle">
      <CardContent className="p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#2E6E5E]/10 flex items-center justify-center">
          <FilePen className="w-10 h-10 text-[#2E6E5E]" />
        </div>
        <h2 className="text-2xl text-[#13231E] font-display font-semibold mb-2">Esperando consentimiento</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Cuando el personal envíe un consentimiento desde el dashboard, aparecerá aquí automáticamente para que el paciente lo firme.
        </p>

        {/* Phase 23.13 — Diagnóstico visible para que el equipo sepa
            si la tablet está bien conectada al servidor en cada momento. */}
        <div className="mt-8 inline-flex flex-col items-center gap-1 text-xs">
          {okBadge && (
            <div className="px-3 py-1.5 rounded-full bg-green-50 text-green-800 border border-green-200" data-testid="public-consent-poll-ok">
              ✓ Conectado · última comprobación {pollStatus.at} · {pollStatus.msg}
            </div>
          )}
          {errBadge && (
            <div className="px-3 py-1.5 rounded-full bg-red-50 text-red-800 border border-red-200 max-w-[600px] break-words" data-testid="public-consent-poll-error">
              ✗ Error conexión · {pollStatus.at}<br/>
              <span className="font-mono text-[10px]">{pollStatus.msg}</span>
            </div>
          )}
          {pollStatus?.ok === null && (
            <div className="text-muted-foreground">Aún no se ha comprobado el servidor…</div>
          )}
          <div className="text-muted-foreground mt-2">
            Sesión: <strong>{user?.name || '—'}</strong> · role <code className="text-[10px]">{user?.role || '—'}</code>
          </div>
        </div>

        {lastSignedAt && (
          <div className="mt-6 text-xs text-muted-foreground">
            Última firma registrada: <strong>{lastSignedAt}</strong>
          </div>
        )}
        <div className="mt-8">
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="public-consent-logout">
            Cerrar sesión de la tablet
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewCard({
  pending, pdfUrl, cambio, avisoLectura, esperaLectura, pdfMal,
  onPdfListo, onPdfFallido, onRecargarVisor, onProceedToSign, onSkip,
}) {
  // El PDF de ESTE documento se ha visto: el `load` del iframe del visor
  // (React lo hace subir hasta aquí) salvo que lo cargado sea la página de
  // error del servidor, con su marca (`esLaPaginaDeError`); o, en pantalla
  // estrecha, donde el visor no pone iframe, que se haya abierto entero con
  // su botón.
  const alCargarElVisor = (e) => {
    const marco = e.target;
    if (!marco || marco.tagName !== 'IFRAME') return;
    if (esLaPaginaDeError(marco)) onPdfFallido();
    else onPdfListo();
  };
  const alAbrirElDocumento = (e) => {
    if (e.target?.closest?.('a[data-testid="public-consent-pdf-abrir"]')) onPdfListo();
  };
  return (
    <Card data-testid="public-consent-review">
      <CardContent className="p-6 space-y-4">
        {/* Aviso FIJO de que el documento ha cambiado a media lectura o firma
            (2ª pasada, 23/9/2026). Antes la pantalla saltaba a la lectura del
            nuevo sin decir nada y la paciente podía seguir como si fuera el
            mismo papel. */}
        {cambio && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3 text-base font-medium text-amber-900"
            data-testid="public-consent-cambio"
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <span>Ha cambiado el documento: ahora es “{cambio.nombre}”. Léelo antes de firmar.</span>
          </div>
        )}
        {/* El servidor la ha devuelto aquí (409 «Abre y lee el documento
            antes de firmarlo», 3ª pasada): su motivo, fijo. */}
        {avisoLectura && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3 text-base font-medium text-amber-900"
            data-testid="public-consent-aviso-lectura"
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <span>{avisoLectura}</span>
          </div>
        )}
        <header>
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-[#13231E]" />
            <h2 className="text-2xl text-[#13231E] font-display font-semibold">{pending.template.name}</h2>
          </div>
          <PatientMeta pending={pending} />
        </header>

        {/* Botón «Abrir el documento completo» + iframe (en la tablet se
            mantiene el iframe; en un móvil, solo el botón). */}
        <div onLoad={alCargarElVisor} onClick={alAbrirElDocumento}>
          <VisorPdfPublico url={pdfUrl} alto={540} testid="public-consent-pdf" />
        </div>
        {pdfMal && (
          <Button variant="outline" onClick={onRecargarVisor} className="h-11" data-testid="public-consent-pdf-reintentar">
            <RefreshCw className="w-4 h-4 mr-2" /> Volver a cargar el documento
          </Button>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" onClick={onSkip} data-testid="public-consent-skip">
            Saltar
          </Button>
          <Button
            onClick={onProceedToSign}
            disabled={!!esperaLectura}
            className="h-12 px-8 text-base bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="public-consent-proceed-to-sign"
          >
            <FilePen className="w-5 h-5 mr-2" /> Continuar a la firma
          </Button>
        </div>
        {/* Por qué no se puede continuar todavía: un botón apagado que no lo
            dice parece roto. */}
        {esperaLectura && (
          <p className="text-sm text-muted-foreground text-right" data-testid="public-consent-espera">
            {esperaLectura}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function SesionCaducadaCard({ onLogout }) {
  return (
    <Card className="border-2 border-amber-400 bg-amber-50" data-testid="public-consent-sesion-caducada">
      <CardContent className="p-12 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-amber-700 mx-auto" />
        <h2 className="text-2xl text-amber-900 font-display font-semibold">{SESION_CADUCADA}.</h2>
        <p className="text-amber-900/80 max-w-md mx-auto">
          La tablet ha dejado de preguntar por consentimientos. Cuando recepción vuelva a entrar,
          pulsa «Recargar».
        </p>
        <Button variant="outline" onClick={onLogout} data-testid="public-consent-sesion-salir">
          Volver a entrar
        </Button>
      </CardContent>
    </Card>
  );
}

function SignatureCard({ pending, sigRef, busy, onBack, onSubmit }) {
  return (
    <Card data-testid="public-consent-sign">
      <CardContent className="p-6 space-y-4">
        <header>
          <h2 className="text-2xl text-[#13231E] font-display font-semibold mb-1">
            Firma del paciente
          </h2>
          <PatientMeta pending={pending} />
        </header>

        <p className="text-sm text-muted-foreground">
          Firma con el dedo o un lápiz táctil dentro del recuadro. Tu firma constituye prueba de tu consentimiento informado para el procedimiento <strong>{pending.template.name}</strong>.
        </p>

        {/* Antes: lienzo fijo de 760 px que desbordaba en el iPad vertical
            (revisión de enlaces 2026-09-07). Ahora ocupa el ancho disponible. */}
        <FirmaDedo
          ref={sigRef}
          testid="public-consent-signature"
          alturaCls="h-56 sm:h-72"
          derecha={(
            <span className="text-xs text-muted-foreground">
              {pending.doc_date ? `Fecha del consent: ${formatDate(pending.doc_date)}` : new Date().toLocaleDateString('es-ES')}
            </span>
          )}
        />

        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onBack}
            className="h-14"
            data-testid="public-consent-back"
            disabled={busy}
          >
            ← Volver a revisar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={busy}
            className="h-14 text-base bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="public-consent-submit"
          >
            {busy
              ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando…</>
              : <><CheckCircle2 className="w-5 h-5 mr-2" /> Firmar y enviar</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DoneCard({ hecho }) {
  return (
    <Card className="border-2 border-green-300 bg-green-50/50" data-testid="public-consent-done">
      <CardContent className="p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-green-700" />
        </div>
        {/* «Firma guardada» cuando el servidor contesta que ya la tenía (la
            respuesta de la primera vez se perdió, 23/9/2026): que la paciente
            sepa que está firmado y que no ha firmado dos veces. */}
        <h2 className="text-2xl text-green-800 font-display font-semibold mb-2">
          {hecho.ya ? 'Firma guardada' : '¡Firma registrada!'}
        </h2>
        <p className="text-green-700/80">
          {hecho.ya
            ? <>La firma de <strong>{hecho.documento || 'este consentimiento'}</strong> ya había llegado: está guardada y no se ha firmado dos veces.</>
            : <>El consentimiento de <strong>{hecho.paciente}</strong> ha quedado firmado correctamente.</>}
        </p>
        <p className="text-xs text-muted-foreground mt-4">Pasando a lo siguiente en unos segundos…</p>
      </CardContent>
    </Card>
  );
}

function PatientMeta({ pending }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
      <div><span className="text-muted-foreground">Paciente:</span> <strong>{pending.patient?.name || '—'}</strong></div>
      {pending.patient?.nif && (
        <div><span className="text-muted-foreground">NIF:</span> {pending.patient.nif}</div>
      )}
      <div><span className="text-muted-foreground">Doctor:</span> <strong>{pending.doctor_name || '—'}</strong></div>
      {pending.doc_date && (
        <div><span className="text-muted-foreground">Fecha consent:</span> {formatDate(pending.doc_date)}</div>
      )}
      {pending.surgery_date && (
        <div><span className="text-muted-foreground">Fecha cirugía:</span> {formatDate(pending.surgery_date)}</div>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('es-ES');
  } catch {
    return iso;
  }
}
